import React, { useState, useEffect, useRef } from 'react';
import {
  DECIMAL_INPUT_PATTERN,
  formatDecimalFieldValue,
  normalizeDecimalTyping,
  parseDecimalFieldInput,
} from '../../utils/decimalInputUtils';
import {
  getDisplayUnitPrice,
  parseUnitPriceForStorage,
  roundPrice,
} from '../../utils/priceIgvUtils';

const toDisplayString = (netPrice, { pricesIncludeIgv, hasIgv, igvRate }) => {
  if (netPrice === '' || netPrice === null || netPrice === undefined) return '';
  const net = parseFloat(netPrice);
  if (!Number.isFinite(net)) return '';
  if (net === 0) return '';
  const displayed = getDisplayUnitPrice(net, { pricesIncludeIgv, hasIgv, igvRate });
  if (displayed === '' || displayed === 0) return displayed === 0 ? '0' : '';
  return formatDecimalFieldValue(displayed);
};

/**
 * Precio unitario como texto (como en guías): acepta coma o punto, hasta 4 decimales.
 * El valor interno que notifica al padre es siempre neto (sin IGV).
 */
const UnitPriceTextInput = ({
  netPrice = 0,
  onNetPriceChange,
  pricesIncludeIgv = false,
  hasIgv = true,
  igvRate = 18,
  disabled = false,
  className = '',
  placeholder = '0.0000',
}) => {
  const [displayValue, setDisplayValue] = useState('');
  const isFocusedRef = useRef(false);

  const igvOptions = { pricesIncludeIgv, hasIgv, igvRate };

  useEffect(() => {
    if (!isFocusedRef.current) {
      setDisplayValue(toDisplayString(netPrice, igvOptions));
    }
  }, [netPrice, pricesIncludeIgv, hasIgv, igvRate]);

  const commitValue = (raw) => {
    const parsed = parseDecimalFieldInput(raw);
    if (parsed.empty) {
      onNetPriceChange(0);
      setDisplayValue('');
      return;
    }
    if (parsed.invalid) {
      setDisplayValue(toDisplayString(netPrice, igvOptions));
      return;
    }
    const net = parseUnitPriceForStorage(String(parsed.value), igvOptions);
    const rounded = roundPrice(net);
    onNetPriceChange(rounded);
    setDisplayValue(toDisplayString(rounded, igvOptions));
  };

  const handleChange = (e) => {
    const raw = normalizeDecimalTyping(e.target.value);
    if (raw === '') {
      setDisplayValue('');
      onNetPriceChange(0);
      return;
    }
    if (!DECIMAL_INPUT_PATTERN.test(raw)) return;
    setDisplayValue(raw);
    const parsed = parseFloat(raw);
    if (!Number.isNaN(parsed)) {
      onNetPriceChange(parseUnitPriceForStorage(raw, igvOptions));
    }
  };

  const handleBlur = () => {
    isFocusedRef.current = false;
    commitValue(displayValue);
  };

  const handleFocus = () => {
    isFocusedRef.current = true;
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      autoComplete="off"
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      disabled={disabled}
      placeholder={placeholder}
      className={className}
    />
  );
};

export default UnitPriceTextInput;
